import React, { useMemo, useState } from "react";
import { useLang, useT, Bi } from "./lang";

/**
 * JUMIBI — Demo booking section.
 * A self-contained, client-side calendar with (faked) available 30-min slots.
 * On submit it POSTs the request to a Power Automate webhook. Because this runs
 * on a static host, the webhook call is a plain browser fetch — no backend.
 * If the browser's CORS preflight is rejected, it transparently retries the
 * POST in fire-and-forget (no-cors) mode so the data still reaches the flow.
 */
const WEBHOOK_URL =
  "https://default84dd272de7b04c41888e09922960e2.7e.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/91e088f842a94ce6956c31d165f5e87f/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=1BhFTAOJg-AmB4U5vnw6u8UCscfqIHzxLDRI66NEdfA";

// Standard 30-min demo slots offered on every working day.
const SLOT_TIMES = ["09:00", "10:30", "13:00", "14:30", "16:00"];
const DAY_MS = 24 * 60 * 60 * 1000;
const BOOKING_WINDOW_DAYS = 60; // how far ahead bookings can be made

const MONTHS = {
  de: ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"],
  en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
};
const DOW = {
  de: ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"],
  en: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
};

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Deterministic pseudo-availability so the calendar looks "real" without a
// backend. Everything is derived from the date, so the picture is stable across
// renders and page reloads. (Runtime Math.sin is fine — only Workflow scripts
// forbid it.)
function hash01(n) {
  const x = Math.sin(n * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}
function daySeed(date) {
  return date.getFullYear() * 372 + (date.getMonth() + 1) * 31 + date.getDate();
}
// How many of the day's slots are still free — shaped to look busy but open:
// ~60% of weekdays well-stocked (green), ~25% almost full (yellow), ~15% booked out.
function freeCount(date) {
  const h = hash01(daySeed(date) * 1.37);
  if (h < 0.15) return 0; // booked out
  if (h < 0.40) return 1 + Math.floor(hash01(daySeed(date) * 3.1) * 2); // 1–2 (almost full)
  return 3 + Math.floor(hash01(daySeed(date) * 5.7) * 3); // 3–5 (plenty)
}
function slotsFor(date) {
  const free = freeCount(date);
  const takenCount = SLOT_TIMES.length - free;
  // Rank slots deterministically and mark the lowest-ranked as taken, so the
  // free times look scattered (not always the same morning/afternoon block).
  const ranked = SLOT_TIMES.map((t, i) => ({ i, r: hash01(daySeed(date) * 13 + i * 97) })).sort(
    (a, b) => a.r - b.r
  );
  const taken = new Set(ranked.slice(0, takenCount).map((s) => s.i));
  return SLOT_TIMES.map((t, i) => ({ time: t, taken: taken.has(i) }));
}
// 'open' = plenty free, 'low' = almost booked, 'full' = booked out.
// null = not a bookable day (weekend, past, or beyond the window).
function dayStatus(date, today, maxDate) {
  const dow = date.getDay(); // 0 = Sun, 6 = Sat
  if (dow === 0 || dow === 6) return null;
  if (date <= today || date > maxDate) return null;
  const f = freeCount(date);
  if (f === 0) return "full";
  if (f <= 2) return "low";
  return "open";
}

export default function BookingSection() {
  const { lang } = useLang();
  const t = useT();

  const today = useMemo(() => startOfDay(new Date()), []);
  const maxDate = useMemo(() => startOfDay(new Date(Date.now() + BOOKING_WINDOW_DAYS * DAY_MS)), []);

  // Currently displayed calendar month
  const [view, setView] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | done | error

  const minMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const maxMonth = new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
  const canPrev = view > minMonth;
  const canNext = view < maxMonth;

  // Build the calendar grid (Mon-first)
  const cells = useMemo(() => {
    const year = view.getFullYear();
    const month = view.getMonth();
    const first = new Date(year, month, 1);
    const lead = (first.getDay() + 6) % 7; // Mon-first offset
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < lead; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(new Date(year, month, d));
    return arr;
  }, [view]);

  const slots = selectedDate ? slotsFor(selectedDate) : [];
  const freeNow = slots.filter((s) => !s.taken).length;

  const updateField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const formatDate = (d) =>
    d
      ? `${DOW[lang][(d.getDay() + 6) % 7]}, ${d.getDate()}. ${MONTHS[lang][d.getMonth()]} ${d.getFullYear()}`
      : "";

  const canSubmit =
    selectedDate && selectedSlot && form.name.trim() && form.email.trim() && form.company.trim() && status !== "sending";

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus("sending");

    const data = {
      name: form.name.trim(),
      company: form.company.trim(),
      email: form.email.trim(),
      phone: form.phone.trim() || "—",
      message: form.message.trim() || "—",
      requestedDate: ymd(selectedDate),
      requestedTime: selectedSlot,
      requestedDateLabel: `${formatDate(selectedDate)} · ${selectedSlot}`,
      language: lang,
      submittedAt: new Date().toISOString(),
    };

    // The connected Power Automate flow posts the request body straight to Teams
    // as an Adaptive Card (it does `string(Body)` and hands it to the Teams "post
    // card" action). So the payload itself must be a valid Adaptive Card — its
    // top-level "type" has to be "AdaptiveCard". All booking data is rendered as
    // TextBlocks / facts so it shows up directly in the Teams message.
    const payload = {
      type: "AdaptiveCard",
      $schema: "http://adaptivecards.io/schemas/adaptive-card.json",
      version: "1.4",
      body: [
        { type: "TextBlock", text: "📅 Neue Demo-Anfrage", weight: "Bolder", size: "Large", wrap: true },
        { type: "TextBlock", text: "jumibi.de — Demo-Buchung", isSubtle: true, spacing: "None", wrap: true },
        {
          type: "FactSet",
          facts: [
            { title: "Name", value: data.name },
            { title: "Unternehmen", value: data.company },
            { title: "E-Mail", value: data.email },
            { title: "Telefon", value: data.phone },
            { title: "Wunschtermin", value: data.requestedDateLabel },
            { title: "Sprache", value: data.language },
            { title: "Eingegangen", value: data.submittedAt },
          ],
        },
        { type: "TextBlock", text: "Nachricht:", weight: "Bolder", spacing: "Medium", wrap: true },
        { type: "TextBlock", text: data.message, wrap: true },
      ],
    };

    try {
      const res = await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok && res.status !== 0) throw new Error("HTTP " + res.status);
      setStatus("done");
    } catch (err) {
      // Most likely a CORS preflight rejection. Retry fire-and-forget so the
      // flow still receives the booking; we can't read the response in this mode.
      try {
        await fetch(WEBHOOK_URL, {
          method: "POST",
          mode: "no-cors",
          headers: { "Content-Type": "text/plain;charset=UTF-8" },
          body: JSON.stringify(payload),
        });
        setStatus("done");
      } catch (err2) {
        console.error("Booking webhook failed:", err2);
        setStatus("error");
      }
    }
  }

  function reset() {
    setStatus("idle");
    setSelectedDate(null);
    setSelectedSlot(null);
    setForm({ name: "", company: "", email: "", phone: "", message: "" });
  }

  return (
    <section className="section section--ice" id="book">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">
            <Bi en="Book a demo" de="Demo buchen" />
          </span>
          <h2 className="h2" style={{ marginTop: 14 }}>
            <Bi en="Pick a slot — see the live system in 30 minutes." de="Termin wählen — das Live-System in 30 Minuten." />
          </h2>
          <p>
            <Bi
              en="Choose an available time and tell us a little about your company. We confirm by email — no transformation theatre, just the running platform and the numbers."
              de="Wählen Sie einen freien Termin und sagen Sie uns ein wenig über Ihr Unternehmen. Wir bestätigen per E-Mail — kein Transformations-Theater, nur die laufende Plattform und die Zahlen."
            />
          </p>
        </div>

        <div className="booking">
          {/* LEFT — calendar + slots */}
          <div className="booking-panel">
            <div className="booking-panel__head">
              <span className="booking-panel__title">
                <Bi en="Select a date" de="Datum wählen" />
              </span>
              <div className="cal-nav">
                <button
                  type="button"
                  aria-label={t("Previous month", "Voriger Monat")}
                  disabled={!canPrev}
                  onClick={() => setView((v) => new Date(v.getFullYear(), v.getMonth() - 1, 1))}
                >
                  ‹
                </button>
                <span className="cal-nav__month">
                  {MONTHS[lang][view.getMonth()]} {view.getFullYear()}
                </span>
                <button
                  type="button"
                  aria-label={t("Next month", "Nächster Monat")}
                  disabled={!canNext}
                  onClick={() => setView((v) => new Date(v.getFullYear(), v.getMonth() + 1, 1))}
                >
                  ›
                </button>
              </div>
            </div>

            <div className="cal-grid">
              {DOW[lang].map((d) => (
                <div className="cal-dow" key={d}>
                  {d}
                </div>
              ))}
              {cells.map((date, i) => {
                if (!date) return <div className="cal-empty" key={"e" + i} />;
                const status = dayStatus(date, today, maxDate);
                const bookable = status === "open" || status === "low";
                const selected = selectedDate && ymd(date) === ymd(selectedDate);
                return (
                  <button
                    type="button"
                    key={ymd(date)}
                    disabled={!bookable}
                    className={
                      "cal-day" +
                      (status ? " cal-day--" + status : "") +
                      (selected ? " cal-day--selected" : "")
                    }
                    onClick={() => {
                      setSelectedDate(date);
                      setSelectedSlot(null);
                    }}
                  >
                    {date.getDate()}
                  </button>
                );
              })}
            </div>

            <div className="cal-legend">
              <span>
                <i className="leg leg--open" /> <Bi en="Many free" de="Viele frei" />
              </span>
              <span>
                <i className="leg leg--low" /> <Bi en="Almost booked" de="Fast ausgebucht" />
              </span>
              <span>
                <i className="leg leg--full" /> <Bi en="Booked out" de="Ausgebucht" />
              </span>
            </div>

            {selectedDate && (
              <div className="slots">
                <div className="slots__label">
                  <Bi en="Available times" de="Freie Uhrzeiten" /> · {formatDate(selectedDate)}
                  {freeNow > 0 && (
                    <span style={{ color: freeNow <= 2 ? "#C77F00" : "var(--green)", marginLeft: 8 }}>
                      · {freeNow} <Bi en="free" de="frei" />
                    </span>
                  )}
                </div>
                <div className="slots__grid">
                  {slots.map((s) => (
                    <button
                      type="button"
                      key={s.time}
                      disabled={s.taken}
                      className={"slot" + (selectedSlot === s.time ? " slot--selected" : "")}
                      onClick={() => setSelectedSlot(s.time)}
                    >
                      {s.time}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT — request form / confirmation */}
          <div className="booking-panel">
            {status === "done" ? (
              <div className="book-done">
                <div className="book-done__check">✓</div>
                <h3>
                  <Bi en="Request received" de="Anfrage eingegangen" />
                </h3>
                <p>
                  <Bi
                    en={`Thanks, ${form.name || "there"} — we'll confirm your slot on ${formatDate(selectedDate)} at ${selectedSlot} by email shortly.`}
                    de={`Danke, ${form.name || ""} — wir bestätigen Ihren Termin am ${formatDate(selectedDate)} um ${selectedSlot} in Kürze per E-Mail.`}
                  />
                </p>
                <button type="button" className="btn btn--ink" onClick={reset}>
                  <Bi en="Book another slot" de="Weiteren Termin buchen" />
                </button>
              </div>
            ) : (
              <form className="book-form" onSubmit={handleSubmit}>
                <span className="booking-panel__title">
                  <Bi en="Your details" de="Ihre Angaben" />
                </span>

                <div className="book-form__sel">
                  {selectedDate && selectedSlot ? (
                    <>
                      <Bi en="Selected" de="Gewählt" />:{" "}
                      <b>
                        {formatDate(selectedDate)} · {selectedSlot}
                      </b>
                    </>
                  ) : (
                    <Bi en="← First pick a date and time on the left." de="← Bitte links zuerst Datum und Uhrzeit wählen." />
                  )}
                </div>

                <div className="field">
                  <label htmlFor="bk-name">
                    <Bi en="Full name *" de="Name *" />
                  </label>
                  <input id="bk-name" type="text" value={form.name} onChange={updateField("name")} required autoComplete="name" />
                </div>

                <div className="field">
                  <label htmlFor="bk-company">
                    <Bi en="Company *" de="Unternehmen *" />
                  </label>
                  <input id="bk-company" type="text" value={form.company} onChange={updateField("company")} required autoComplete="organization" />
                </div>

                <div className="field--row">
                  <div className="field">
                    <label htmlFor="bk-email">
                      <Bi en="Business email *" de="Geschäftliche E-Mail *" />
                    </label>
                    <input id="bk-email" type="email" value={form.email} onChange={updateField("email")} required autoComplete="email" />
                  </div>
                  <div className="field">
                    <label htmlFor="bk-phone">
                      <Bi en="Phone" de="Telefon" />
                    </label>
                    <input id="bk-phone" type="tel" value={form.phone} onChange={updateField("phone")} autoComplete="tel" />
                  </div>
                </div>

                <div className="field">
                  <label htmlFor="bk-msg">
                    <Bi en="What would you like to see? (ERP, modules…)" de="Was möchten Sie sehen? (ERP, Module…)" />
                  </label>
                  <textarea id="bk-msg" rows={3} value={form.message} onChange={updateField("message")} />
                </div>

                {status === "error" && (
                  <div className="book-form__msg book-form__msg--err">
                    <Bi
                      en="Something went wrong sending your request. Please try again or email info@jumibi.de."
                      de="Beim Senden ist etwas schiefgelaufen. Bitte erneut versuchen oder an info@jumibi.de schreiben."
                    />
                  </div>
                )}

                <button type="submit" className="btn btn--primary" disabled={!canSubmit}>
                  {status === "sending" ? (
                    <Bi en="Sending…" de="Wird gesendet…" />
                  ) : (
                    <Bi en="Request this slot →" de="Termin anfragen →" />
                  )}
                </button>

                <p className="book-form__hint">
                  <Bi
                    en="By submitting you agree we may contact you about this request. * required."
                    de="Mit dem Absenden stimmen Sie zu, dass wir Sie zu dieser Anfrage kontaktieren dürfen. * Pflichtfeld."
                  />
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
